import type { RoleItem, RolesListParams, Permission, PermissionsListParams } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { createPaginationParams, createTableQueryOptions } from '@/components/table';
import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';

// Atom for roles list parameters
export const rolesListParamsAtom = atom<RolesListParams>({
  page: 1,
  per_page: 50, // Get more roles for autocomplete
  filters: {}
});

// Hydration flag to delay initial query until URL/filters are synced
export const rolesHydratedAtom = atom<boolean>(false);

// Main roles query atom
export const rolesQueryAtom = atomWithQuery((get) => {
  const params = get(rolesListParamsAtom);
  const hydrated = get(rolesHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['roles', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page
        },
        params.filters
      );

      const { data } = await apiClient.get<{
        success: boolean;
        message: string;
        data: RoleItem[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      }>('/roles', { params: apiParams });

      return data;
    },
    enabled: hydrated
  });
});

// Atom for permissions list parameters
export const permissionsListParamsAtom = atom<PermissionsListParams>({
  page: 1,
  per_page: 50, // Get more permissions for autocomplete
  filters: {
    name: undefined // For search functionality
  }
});

// Permissions query atom for autocomplete
export const permissionsQueryAtom = atomWithQuery((get) => {
  const params = get(permissionsListParamsAtom);

  return createTableQueryOptions({
    queryKey: ['permissions', params.filters.name || 'all'] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page
        },
        params.filters
      );

      const { data } = await apiClient.get<{
        success: boolean;
        message: string;
        data: Permission[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      }>('/permissions', { params: apiParams });

      return data;
    },
    enabled: true // Always enabled for autocomplete
  });
});

// Create role atom
export const createRole = atom(
  null,
  async (get, set, formData: { name: string; description: string; permissions: number[] }) => {
    const payload = {
      name: formData.name,
      description: formData.description,
      permissions: formData.permissions
    };

    await apiClient.post('/roles', payload);

    // Trigger refresh by updating params
    const currentParams = get(rolesListParamsAtom);
    set(rolesListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Update role atom (unified for name, description, and permissions)
export const updateRole = atom(
  null,
  async (get, set, { id, name, description, permissions }: { id: number; name: string; description: string; permissions: number[] }) => {
    const payload = {
      name: name,
      description: description,
      permissions: permissions
    };

    await apiClient.put(`/roles/${id}`, payload);

    // Trigger refresh by updating params
    const currentParams = get(rolesListParamsAtom);
    set(rolesListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Delete role atom
export const deleteRole = atom(
  null,
  async (get, set, id: number) => {
    await apiClient.delete(`/roles/${id}`);

    // Trigger refresh by updating params
    const currentParams = get(rolesListParamsAtom);
    set(rolesListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Detach permission from role atom
export const detachPermissionFromRole = atom(
  null,
  async (get, set, { roleId, body }: { roleId: number, body: { permission_ids: number[] } }) => {
    await apiClient.post(`/roles/${roleId}/detach-permissions`, body);

    // Trigger refresh by updating params
    const currentParams = get(rolesListParamsAtom);
    set(rolesListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);
